/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  detectPlatform,
  iosVersion,
  resolveState,
  supported,
  currentSubscription,
  fetchKey,
  refresh,
  enable as enablePush,
  disable as disablePush,
  type Platform,
  type PushState,
} from './push';

/**
 * The one place that knows whether push is on, and the only thing that changes
 * it.
 *
 * **Deliberately not react-query.** Nothing here is server state: the answer is
 * assembled from `Notification.permission`, the service worker registration and
 * the user agent, none of which have a cache key, none of which any other tab
 * can invalidate, and all of which a `useQuery` would re-fetch on window focus
 * for no reason. The one genuinely remote fact — whether the server has VAPID keys —
 * cannot change while the app is open.
 *
 * It is read from two places (the account menu row and the introduction card)
 * and both must agree, so the hook re-reads after every change rather than
 * keeping a second copy of the answer.
 */
export interface Push {
  state: PushState;
  platform: Platform;
  /** Still working out the answer. The row renders nothing rather than flickering. */
  loading: boolean;
  /** Mid-flight on a turn-on or turn-off, so the switch can refuse a second press. */
  busy: boolean;
  enable: () => Promise<boolean>;
  disable: () => Promise<void>;
}

export function usePush(): Push {
  const [state, setState] = useState<PushState>('unsupported');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const platform = detectPlatform(
    typeof navigator === 'undefined' ? '' : navigator.userAgent,
    typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
  );

  /**
   * The component can unmount while `read` is awaiting the server; setting state
   * then is a warning in development and a leak in principle.
   *
   * **`alive.current = true` in the effect body, and it is not optional.**
   * `<StrictMode>` mounts, runs the cleanup, and mounts again — so without this
   * line the first cleanup set the ref to false and nothing ever set it back.
   * `read()` then completed, saw a dead component, and returned before
   * `setLoading(false)`: `loading` stayed true for ever and **the account menu
   * row never rendered at all**.
   *
   * It is a development-only double-invoke, which is why the browser suite —
   * which runs the production build — was green while the row was missing on
   * localhost. A ref initialised at declaration is initialised once; a
   * re-mounted component needs it set again on mount.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const read = useCallback(async () => {
    const isSupported = supported();
    // **No early return for an unsupported browser**, which this had. On a real
    // iPhone Safari tab `supported` is false — that is precisely how a tab is
    // told apart from an installed app — and short-circuiting to 'unsupported'
    // gave those people "not available in this browser" instead of the four
    // steps that would have made it work. `resolveState` is the only thing
    // allowed to turn facts into a state.
    const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
    let configured = false;
    try {
      configured = (await fetchKey()).enabled;
    } catch {
      // An install with no keys, or the request failing. Either way there is
      // nothing to subscribe to, and `unsupported` is the honest answer — the
      // dialog says so plainly instead of offering a button that cannot work.
      configured = false;
    }
    const sub = await currentSubscription();

    if (!alive.current) return;
    setState(resolveState({
      supported: isSupported,
      configured,
      // Guarded, because this runs on a browser that may not have Notification
      // at all — reading `.permission` off `undefined` would throw here and
      // leave `loading` true, which is the row never appearing.
      permission: isSupported ? Notification.permission : 'default',
      platform: detectPlatform(ua, navigator.maxTouchPoints),
      iosVersion: iosVersion(ua),
      subscribed: !!sub,
    }));
    setLoading(false);
  }, []);

  useEffect(() => {
    void read();
    // **Re-sent on every load, not only when the switch is pressed.** Browsers
    // rotate endpoints, and a subscription nobody refreshes dies quietly — the
    // switch keeps saying on while nothing arrives, which is the worst failure
    // available because nothing anywhere reports it.
    void refresh();
  }, [read]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const ok = await enablePush();
      await read();
      return ok;
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [read]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await disablePush();
      await read();
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [read]);

  return { state, platform, loading, busy, enable, disable };
}
